import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Building2, Users, ShieldCheck, Power, Trash2, LogOut, Loader2, Slack as SlackIcon,
  CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react'
import { platformApi, type OrgSummary, type FeatureInfo } from '@/api/platform'
import { usePlatformStore } from '@/stores/platformStore'

export default function PlatformConsole() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { admin, logout } = usePlatformStore()
  const [deleteTarget, setDeleteTarget] = useState<OrgSummary | null>(null)

  const { data: stats } = useQuery({ queryKey: ['platform', 'stats'], queryFn: platformApi.stats })
  const { data: features = [] } = useQuery({ queryKey: ['platform', 'features'], queryFn: platformApi.features })
  const { data: orgs = [], isLoading } = useQuery({ queryKey: ['platform', 'orgs'], queryFn: platformApi.listOrgs })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['platform', 'orgs'] })
    qc.invalidateQueries({ queryKey: ['platform', 'stats'] })
  }

  const toggleFeature = useMutation({
    mutationFn: ({ org, key }: { org: OrgSummary; key: string }) => {
      const disabled = new Set(org.disabled_features)
      if (disabled.has(key)) disabled.delete(key)
      else disabled.add(key)
      return platformApi.setFeatures(org.id, Array.from(disabled))
    },
    onSuccess: invalidate,
  })

  const setActive = useMutation({
    mutationFn: ({ org, active }: { org: OrgSummary; active: boolean }) =>
      active ? platformApi.activate(org.id) : platformApi.deactivate(org.id),
    onSuccess: invalidate,
  })

  const handleLogout = () => { logout(); navigate('/login') }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-sidebar text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-6 w-6 text-brand-light" />
            <div>
              <p className="text-sm font-semibold leading-tight">Platform Admin</p>
              <p className="text-xs text-white/50 leading-tight">Super-admin console</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {admin && <span className="hidden text-sm text-white/70 sm:inline">{admin.name || admin.email}</span>}
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat icon={Building2} label="Organizations" value={stats?.total_orgs} />
          <Stat icon={CheckCircle2} label="Active orgs" value={stats?.active_orgs} />
          <Stat icon={Users} label="Total users" value={stats?.total_users} />
          <Stat icon={Users} label="Active users" value={stats?.active_users} />
        </div>

        <h2 className="mt-10 mb-4 text-lg font-bold text-gray-900">Organizations</h2>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : orgs.length === 0 ? (
          <div className="card p-12 text-center text-sm text-gray-400">No organizations yet.</div>
        ) : (
          <div className="space-y-4">
            {orgs.map((org: OrgSummary) => (
              <div key={org.id} className={`card p-5 ${!org.is_active ? 'opacity-70' : ''}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{org.name}</h3>
                        {org.is_active ? (
                          <span className="badge bg-green-100 text-green-700">Active</span>
                        ) : (
                          <span className="badge bg-gray-200 text-gray-600">Revoked</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        /{org.slug} · {org.user_count} user{org.user_count === 1 ? '' : 's'}
                        {org.slack_connected && (
                          <span className="ml-2 inline-flex items-center gap-1 text-gray-400">
                            <SlackIcon className="h-3 w-3" /> Slack
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActive.mutate({ org, active: !org.is_active })}
                      disabled={setActive.isPending}
                      className={`btn btn-sm ${org.is_active ? 'btn-outline' : 'btn-primary'}`}
                    >
                      <Power className="h-4 w-4" /> {org.is_active ? 'Revoke' : 'Restore'}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(org)}
                      className="btn btn-sm btn-danger"
                    >
                      <Trash2 className="h-4 w-4" /> Delete
                    </button>
                  </div>
                </div>

                {/* Feature toggles */}
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Feature access</p>
                  <div className="flex flex-wrap gap-2">
                    {features.map((f: FeatureInfo) => {
                      const on = !org.disabled_features.includes(f.key)
                      const pending = toggleFeature.isPending && toggleFeature.variables?.org.id === org.id && toggleFeature.variables?.key === f.key
                      return (
                        <button
                          key={f.key}
                          onClick={() => toggleFeature.mutate({ org, key: f.key })}
                          disabled={toggleFeature.isPending}
                          className={
                            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
                            (on
                              ? 'border-brand/30 bg-brand/10 text-brand-dark hover:bg-brand/20'
                              : 'border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100')
                          }
                        >
                          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : on ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          {f.label}
                        </button>
                      )
                    })}
                  </div>
                  <p className="mt-2 text-xs text-gray-400">Click a module to grant or revoke it for this organization.</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {deleteTarget && (
        <DeleteModal org={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={() => { setDeleteTarget(null); invalidate() }} />
      )}
    </div>
  )
}

function Stat({ icon: Icon, label, value }: { icon: any; label: string; value?: number }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 text-gray-400"><Icon className="h-4 w-4" /><span className="text-xs font-medium">{label}</span></div>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value ?? '—'}</p>
    </div>
  )
}

function DeleteModal({ org, onClose, onDeleted }: { org: OrgSummary; onClose: () => void; onDeleted: () => void }) {
  const [text, setText] = useState('')
  const del = useMutation({
    mutationFn: () => platformApi.deleteOrg(org.id, text),
    onSuccess: onDeleted,
  })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Permanently delete “{org.name}”?</h3>
            <p className="mt-1 text-sm text-gray-500">
              This erases the organization and <strong>all</strong> of its data — users, attendance,
              leave, payroll, everything. This cannot be undone.
            </p>
          </div>
        </div>

        <label className="mt-4 block text-sm text-gray-600">
          Type <code className="rounded bg-gray-100 px-1 font-semibold text-gray-800">{org.slug}</code> to confirm:
        </label>
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="input mt-1"
          placeholder={org.slug}
        />
        {del.isError && <p className="mt-2 text-sm text-red-600">{(del.error as any)?.response?.data?.detail || 'Delete failed'}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-sm btn-outline">Cancel</button>
          <button
            onClick={() => del.mutate()}
            disabled={text !== org.slug || del.isPending}
            className="btn btn-sm btn-danger"
          >
            {del.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  )
}
